import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Language } from '../contexts/LanguageContext';
interface LandingPageProps {
  onSelectLanguage: (lang: Language) => void;
  onConsularAccess?: (lang: Language) => void;
}
export function LandingPage({
  onSelectLanguage,
  onConsularAccess
}: LandingPageProps) {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <div className="fixed inset-0 z-50 flex flex-col items-center justify-start sm:justify-center overflow-y-auto overflow-x-hidden hero-bg" data-id="element-2677" data-mp="awcfdy1yw4om">
      {/* Decorative Orbs */}
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-haa-skyblue/15 rounded-full blur-3xl orb-1" data-id="element-2678" data-mp="1z22k89i47hl" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-haa-midblue/12 rounded-full blur-3xl orb-2" data-id="element-2679" data-mp="2ngnpjthno5ys" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-haa-blue/8 rounded-full blur-3xl orb-3" data-id="element-2680" data-mp="2sa8dsttg5lks" />
      <div className="absolute top-1/3 right-1/3 w-48 h-48 bg-haa-skyblue/10 rounded-full blur-2xl orb-4" data-id="element-2681" data-mp="2iiuhsw0pf3we" />
      <div className="absolute bottom-1/3 left-1/3 w-56 h-56 bg-haa-midblue/10 rounded-full blur-2xl orb-5" data-id="element-2682" data-mp="3686qzvw17byp" />

      {/* Content Container */}
      <motion.div variants={containerVariants} initial="hidden" animate="visible" className="relative z-10 flex flex-col items-center text-center px-4 w-full max-w-4xl pt-28 pb-12 sm:pt-32 sm:pb-0" data-id="element-2683" data-mp="3b79lwnias1sy">
        {/* Branding */}
        <motion.div variants={itemVariants} className="mb-8 sm:mb-12 flex flex-col items-center relative" data-id="element-2684" data-mp="1lsljnf3f808n">
          {/* Soft glow behind logo for contrast */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[140%] bg-haa-skyblue/5 rounded-full blur-3xl pointer-events-none" data-id="element-2685" data-mp="3shn8pc0tzdaq" />
          <img src="https://cdn.magicpatterns.com/uploads/b1GB8Z9aEvoE58p9BdX4qx/HonduranAmericanLogoNew.png" alt="Honduran American Association" className="w-64 sm:w-80 md:w-96 mb-4 relative z-10 drop-shadow-[0_2px_20px_rgba(0,63,135,0.15)]" data-id="element-2686" data-mp="3nkdab6vgrrar" />
          <span className="font-nunito text-haa-midblue text-sm uppercase tracking-[0.3em] font-bold relative z-10" data-id="element-2687" data-mp="39ojaregkyeab">
            Est. 2025
          </span>
        </motion.div>

        {/* Welcome Text */}
        <motion.div variants={itemVariants} className="mb-8 sm:mb-12" data-id="element-2688" data-mp="2zuj1d0e9mmvs">
          <h2 className="font-bebas text-6xl sm:text-7xl md:text-8xl lg:text-9xl text-haa-navy tracking-wider leading-[0.85] mb-2" data-id="element-2689" data-mp="2vsfkfw8tuq34">
            Welcome
          </h2>
          <h2 className="font-bebas text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-haa-blue tracking-wider leading-[0.85]" data-id="element-2690" data-mp="tz87uuklmmkx">
            Bienvenidos
          </h2>
        </motion.div>

        {/* Language Buttons */}
        <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-center justify-center gap-6 w-full max-w-md sm:max-w-none" data-id="element-2691" data-mp="2guwn3t3uhpzh">
          <motion.button whileHover={{
          scale: 1.05,
          y: -4
        }} whileTap={{
          scale: 0.95
        }} onClick={() => onSelectLanguage('EN')} className="w-full sm:w-64 py-5 px-8 bg-haa-navy rounded-2xl shadow-[0_8px_30px_rgba(0,26,62,0.2)] hover:shadow-[0_20px_40px_rgba(0,26,62,0.3)] transition-shadow duration-300 flex flex-col items-center justify-center group border border-haa-navy/10" data-id="element-2692" data-mp="2pp1au3nomlas">
            <span className="font-bebas text-3xl text-white tracking-wider group-hover:text-haa-skyblue transition-colors" data-id="element-2693" data-mp="3devhu4lblfn4">
              English
            </span>
            <span className="font-nunito text-sm text-white/60 mt-1" data-id="element-2694" data-mp="3gw01vbaev3vc">
              Continue in English
            </span>
          </motion.button>

          <motion.button whileHover={{
          scale: 1.05,
          y: -4
        }} whileTap={{
          scale: 0.95
        }} onClick={() => onSelectLanguage('ES')} className="w-full sm:w-64 py-5 px-8 bg-haa-blue rounded-2xl shadow-[0_8px_30px_rgba(0,63,135,0.2)] hover:shadow-[0_20px_40px_rgba(0,63,135,0.3)] transition-shadow duration-300 flex flex-col items-center justify-center group border border-haa-blue/10" data-id="element-2695" data-mp="3by8b4kli5nsq">
            <span className="font-bebas text-3xl text-white tracking-wider group-hover:text-haa-skyblue transition-colors" data-id="element-2696" data-mp="ddzthv0y0wh7">
              Español
            </span>
            <span className="font-nunito text-sm text-white/60 mt-1" data-id="element-2697" data-mp="mfg2pzg0usju">
              Continuar en Español
            </span>
          </motion.button>
        </motion.div>

        {/* WhatsApp Contact */}
        <motion.div variants={itemVariants} className="mt-6 flex flex-col items-center" data-id="element-2698" data-mp="1126a9ikc9hdv">
          <a href="https://wa.me/17033131465" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 font-nunito text-haa-navy/70 hover:text-haa-navy text-lg font-bold transition-colors" data-id="element-2699" data-mp="160pwzl6rax7a">
            <svg className="w-6 h-6 text-green-600 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor" data-id="element-2700" data-mp="ibomlsed5bzd">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-2701" data-mp="n4q5scllrxor" />
            </svg>
            +1-703-313-1465
          </a>
        </motion.div>

        {/* Consular Affairs Quick Access */}
        <motion.div variants={itemVariants} className="mt-10 pb-12 flex flex-col items-center gap-3" data-id="element-2702" data-mp="14lndt6uu5o19">
          <p className="font-nunito text-haa-navy/40 text-xs uppercase tracking-[0.2em]" data-id="element-2703" data-mp="znlssgdunen5">
            Consular Services <span className="mx-1.5 opacity-50" data-id="element-2704" data-mp="3b71fkmde9ax2">/</span>{' '}
            Servicios Consulares
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3" data-id="element-2705" data-mp="3g5l6z2arbewt">
            <motion.button whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={() => onConsularAccess?.('EN')} className="inline-flex items-center gap-2 bg-haa-midblue/10 backdrop-blur-sm border border-haa-midblue/20 text-haa-navy font-nunito font-semibold text-sm px-6 py-3 rounded-full hover:bg-haa-midblue/20 hover:border-haa-midblue/40 transition-all duration-200" data-id="element-2706" data-mp="3k7g6q2d1hk8h">
              <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2707" data-mp="29jikz5ub0vbz">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" data-id="element-2708" data-mp="1ppsdexpw7xkl" />
                <polyline points="14 2 14 8 20 8" data-id="element-2709" data-mp="1pny1ua0ihht6" />
                <line x1="16" y1="13" x2="8" y2="13" data-id="element-2710" data-mp="23g47h261g6ed" />
                <line x1="16" y1="17" x2="8" y2="17" data-id="element-2711" data-mp="1zfk9jwdn4yr6" />
              </svg>
              Consular Affairs in English
            </motion.button>
            <motion.button whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={() => onConsularAccess?.('ES')} className="inline-flex items-center gap-2 bg-haa-midblue/10 backdrop-blur-sm border border-haa-midblue/20 text-haa-navy font-nunito font-semibold text-sm px-6 py-3 rounded-full hover:bg-haa-midblue/20 hover:border-haa-midblue/40 transition-all duration-200" data-id="element-2712" data-mp="1br0zpcey0dwz">
              <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2713" data-mp="2r68953zo1w2q">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" data-id="element-2714" data-mp="2pqw155efslsl" />
                <polyline points="14 2 14 8 20 8" data-id="element-2715" data-mp="2upqjy7cemacp" />
                <line x1="16" y1="13" x2="8" y2="13" data-id="element-2716" data-mp="2uqierfy6g41x" />
                <line x1="16" y1="17" x2="8" y2="17" data-id="element-2717" data-mp="3ifkugi993mts" />
              </svg>
              Asuntos Consulares en Español
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </div>;
}